"use client";

import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { ThemeSection, ThemeText, AuroraBlobs } from "@/components/ui/theme-utils";

type Testimonial = {
  id: string;
  company: string;
  quote: string;
  author: string;
  role: string;
  logo: string;
};

const TESTIMONIALS: Testimonial[] = [
  {
    id: "t2",
    company: "Twelvef Moments",
    quote:
      "The mid-file resume feature has saved us countless hours. Our team can focus on creativity instead of uploads.",
    author: "Aravind",
    role: "Creative Director",
    logo: "/images/testimonials/aravind.png"
  },
  {
    id: "t3",
    company: "Salty Zebra",
    quote:
      "Security was our primary concern, and Zset delivered beyond our expectations. The encrypted packets give us peace of mind.",
    author: "Jess",
    role: "Head of Production",
    logo: "/images/testimonials/salty.png"
  }
];

export function Testimonials() {
  const { isDark } = useTheme();

  return (
    <ThemeSection id="testimonials" pattern="dots">
      <AuroraBlobs variant="pink" />
      <div className="container mx-auto px-5 sm:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-20">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium mb-5",
              isDark
                ? "bg-white/[0.04] border border-white/10 text-white/80"
                : "bg-white border border-stone-200 text-stone-700"
            )}
          >
            <Quote className="h-3.5 w-3.5 text-brand-500" />
            Loved by teams
          </span>
          <h2 className="responsive-heading-2 mb-5">
            Trusted by industry <span className="text-gradient">leaders</span>
          </h2>
          <ThemeText variant="subtle" className="responsive-text-lg">
            See what our clients say about their day-to-day with Zset.
          </ThemeText>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 max-w-3xl mx-auto">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure
              key={t.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={cn(
                "group relative rounded-3xl p-7 transition-all duration-300",
                isDark
                  ? "bg-white/[0.025] border border-white/[0.08] hover:border-white/15 hover:shadow-glow"
                  : "bg-white border border-stone-200/80 hover:border-stone-300/80 hover:shadow-elevated"
              )}
            >
              <Quote
                className={cn(
                  "h-7 w-7 mb-4",
                  isDark ? "text-brand-400/60" : "text-brand-500/60"
                )}
              />
              <blockquote className="text-base sm:text-lg leading-relaxed font-medium">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-6 pt-6 border-t border-stone-200/60 dark:border-white/5 flex items-center gap-3">
                <img
                  src={t.logo}
                  alt={`${t.company} logo`}
                  className="h-10 w-10 rounded-full object-cover ring-2 ring-white/10"
                />
                <div>
                  <div className="font-semibold text-sm">{t.author}</div>
                  <div className="text-xs text-stone-500">
                    {t.role} · {t.company}
                  </div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </ThemeSection>
  );
}
